import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import SmartImage from './SmartImage';
import {
  ArrowRight,
  Users,
  Award,
  CheckCircle,
  ChevronRight,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ProgramsSection = ({ config }) => {
  if (!config) return null;

  const {
    homepage_title = 'Programs & Pathways',
    homepage_subtitle = 'Safety-first swim education with structured progression',
    homepage_cta_text = 'Explore All Programs',
    homepage_featured_count = 3,
    pathways = [],
  } = config;

  // Get featured/published pathways only
  const featuredPathways = pathways
    .filter(p => p.status === 'published' && p.featured)
    .sort((a, b) => (a.order || 0) - (b.order || 0))
    .slice(0, Math.min(homepage_featured_count, 3)); // Max 3 featured

  const getImageUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('http')) return url;
    return `${BACKEND_URL}${url}`;
  };

  if (featuredPathways.length === 0) return null;

  return (
    <section className="py-20 bg-white" data-testid="programs-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <Badge className="mb-4 bg-primary/10 text-primary border-primary/20">Find Your Path</Badge>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mb-4">
            {homepage_title}
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            {homepage_subtitle}
          </p>
        </div>

        {/* Featured Pathways Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {featuredPathways.map((pathway) => (
            <Card 
              key={pathway.id} 
              className="group border-0 shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden"
            >
              {/* Image */}
              <div className="aspect-[16/10] relative overflow-hidden bg-gradient-to-br from-primary/20 to-cyan-500/20">
                {pathway.image_url ? (
                  <SmartImage
                    src={getImageUrl(pathway.image_url)}
                    alt={pathway.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <Users className="w-16 h-16 text-primary/30" />
                  </div>
                )}
                {/* Outcome Badge */}
                {pathway.outcome_badge && (
                  <div className="absolute top-3 right-3">
                    <Badge className="bg-white/90 text-primary border-0 shadow-sm">
                      <Award className="w-3 h-3 mr-1" />
                      {pathway.outcome_badge}
                    </Badge>
                  </div>
                )}
              </div>

              <CardContent className="p-6">
                <h3 className="font-heading text-xl font-bold text-slate-900 mb-2 group-hover:text-primary transition-colors">
                  {pathway.title}
                </h3>
                <p className="text-sm text-primary font-medium mb-3">
                  {pathway.best_for}
                </p>
                <p className="text-slate-600 text-sm mb-4 line-clamp-2">
                  {pathway.tagline}
                </p>

                {/* Key outcomes preview */}
                {pathway.bullets && pathway.bullets.length > 0 && (
                  <div className="space-y-1.5 mb-4">
                    {pathway.bullets.slice(0, 2).map((bullet, idx) => (
                      <div key={idx} className="flex items-center gap-2 text-sm text-slate-600">
                        <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                        <span className="line-clamp-1">{bullet}</span>
                      </div>
                    ))}
                  </div>
                )}

                <Link to="/programs" className="inline-flex items-center text-primary font-medium text-sm hover:underline group/link">
                  View Pathway
                  <ChevronRight className="w-4 h-4 ml-1 group-hover/link:translate-x-1 transition-transform" />
                </Link>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* CTA */}
        <div className="text-center">
          <Link to="/programs">
            <Button
              size="lg"
              className="bg-primary hover:bg-primary/90 text-white font-semibold px-8 py-6 rounded-xl shadow-lg hover:shadow-xl transition-all group"
            >
              {homepage_cta_text}
              <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
};

export default ProgramsSection;
